import { Button, Grid, LinearProgress, makeStyles, Tab, Tabs, Typography } from "@material-ui/core";
import {
  CameraAltOutlined as CameraAltOutlinedIcon,
  Edit as EditIcon,
  Instagram as InstagramIcon,
  Launch as LaunchIcon,
  LinkedIn as LinkedInIcon
} from "@material-ui/icons";
import { useQuery } from "@redux-requests/react";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { BaseLayout, HeadBar } from "../components";
import EditImageDialog from "../components/EditImageDialog";
import { types } from "../ducks";
import { getExpert } from "../ducks/actions";
import { EPostersTab, ExpertProfileTab, SessionsTab } from "../features/experts";
import EditExpertDialog from "../features/experts/EditExpertDialog";
import useExpertOwner from "../hooks/useExpertOwner";

const useStyles = makeStyles(theme => ({
  root: {
    "& > *": {
      marginBottom: theme.spacing(3)
    }
  },
  coverContainer: {
    position: "relative"
  },
  cover: {
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[3],
    width: "100%",
    backgroundColor: theme.palette.primary.light
  },
  coverEmpty: {
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[3],
    width: "100%",
    height: theme.spacing(32),
    objectFit: "cover",
    backgroundColor: theme.palette.primary.light
  },
  publicAvatarContainer: {
    position: "relative",
    width: "160px"
  },
  publicAvatar: {
    marginTop: -theme.spacing(10),
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[3],
    height: theme.spacing(20),
    width: theme.spacing(20),
    objectFit: "contain",
    backgroundColor: "white",
    padding: 4
  },
  expertInfo: {
    paddingLeft: theme.spacing(3)
  },
  headbarToolbar: {
    [theme.breakpoints.down(767)]: {
      textAlign: "center"
    },
    marginBottom: theme.spacing(1)
  },
  rightToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    "& > *": {
      marginBottom: theme.spacing(1)
    }
  },
  socialButtonBar: {
    display: "flex",
    "& > *": {
      marginRight: theme.spacing(1)
    }
  },
  iconButton: {
    padding: 0
  },
  textFormatted: {
    whiteSpace: "pre-line"
  },
  cameraButton: {
    borderRadius: "0.25rem",
    height: "2rem",
    width: "2rem",
    padding: "0.25rem",
    background: "rgba(0, 0, 0, 0.3)",
    position: "absolute",
    bottom: "1rem",
    right: "1rem",
    cursor: "pointer"
  }
}));

const ExpertPage = () => {
  const classes = useStyles();
  const intl = useIntl();
  const { id } = useParams();
  const dispatch = useDispatch();
  const [editExpertDialog, setEditExpertDialog] = useState(false);
  const [editCoverDialog, setEditCoverDialog] = useState(false);
  const [editPublicAvatarDialog, setEditPublicAvatarDialog] = useState(false);

  const [tab, setTab] = useState("profile");

  const onTabChange = (e, newTab) => {
    setTab(newTab);
  };

  useEffect(() => {
    dispatch(getExpert({ id }));
  }, [dispatch, id]);

  const {
    data: { data: expert },
    loading
  } = useQuery({ type: types.GET_EXPERT, defaultData: { data: {} } });

  const isExpertOwner = useExpertOwner({ expertId: id });

  return (
    <BaseLayout className={classes.root}>
      {loading ? (
        <LinearProgress />
      ) : (
        <>
          <div className={classes.coverContainer}>
            {expert.cover ? (
              <img src={expert.cover} alt={expert.publicName} className={classes.cover} />
            ) : (
              <div className={classes.coverEmpty} />
            )}
            {isExpertOwner && (
              <CameraAltOutlinedIcon
                className={classes.cameraButton}
                onClick={() => {
                  setEditCoverDialog(true);
                }}
              />
            )}
          </div>
          <HeadBar>
            <Grid container>
              <Grid item xs={12} sm={6} md={8} lg={9} className={classes.headbarToolbar}>
                <div className={classes.expertInfo}>
                  <div className={classes.publicAvatarContainer}>
                    {expert.publicAvatar ? (
                      <img
                        src={expert.publicAvatar}
                        alt={expert.publicName}
                        className={classes.publicAvatar}
                      />
                    ) : (
                      <div className={classes.publicAvatar} />
                    )}
                    {isExpertOwner && (
                      <CameraAltOutlinedIcon
                        className={classes.cameraButton}
                        onClick={() => {
                          setEditPublicAvatarDialog(true);
                        }}
                      />
                    )}
                  </div>
                  <Typography variant="h4">
                    <strong>{expert.publicName}</strong>
                  </Typography>
                  <Typography variant="body1" paragraph className={classes.textFormatted}>
                    {expert.shortDescription}
                  </Typography>
                  <div className={classes.socialButtonBar}>
                    {expert.websiteUrl && (
                      <Button
                        endIcon={<LaunchIcon />}
                        href={expert.websiteUrl}
                        target="_blank"
                        variant="outlined"
                        color="primary"
                        size="large"
                      >
                        {intl.formatMessage({
                          id: "Pages.ExpertPage.Website",
                          defaultMessage: "Website"
                        })}
                      </Button>
                    )}
                    {expert.linkedinUrl && (
                      <Button
                        className={classes.iconButton}
                        href={expert.linkedinUrl}
                        target="_blank"
                        variant="outlined"
                        color="primary"
                        size="medium"
                      >
                        <LinkedInIcon />
                      </Button>
                    )}
                    {expert.instagramUrl && (
                      <Button
                        className={classes.iconButton}
                        href={expert.instagramUrl}
                        target="_blank"
                        variant="outlined"
                        color="primary"
                        size="medium"
                      >
                        <InstagramIcon />
                      </Button>
                    )}
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} className={classes.rightToolbar}>
                {isExpertOwner && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setEditExpertDialog(true);
                    }}
                  >
                    <EditIcon />
                    &nbsp;&nbsp;&nbsp;
                    {intl.formatMessage({
                      id: "Pages.ExhibitorPage.EditProfileInfo",
                      defaultMessage: "Edit Profile Info"
                    })}
                  </Button>
                )}
              </Grid>
            </Grid>
          </HeadBar>
          <Tabs value={tab} onChange={onTabChange} indicatorColor="primary">
            <Tab
              value="profile"
              label={intl.formatMessage({
                id: "Pages.ExpertPage.ExpertProfile",
                defaultMessage: "Expert Profile"
              })}
            />
            {expert.user &&
              (expert.user.roles?.includes("speaker") ||
                expert.user.roles?.includes("speaker")) && (
                <Tab
                  value="library"
                  label={intl.formatMessage({
                    id: "Pages.ExpertPage.Library",
                    defaultMessage: "Library"
                  })}
                />
              )}
            {expert.user && expert.user.roles?.includes("accademic") && (
              <Tab
                value="eposters"
                label={intl.formatMessage({
                  id: "Pages.ExpertPage.EPosters",
                  defaultMessage: "E-Posters"
                })}
              />
            )}
          </Tabs>
          {tab === "profile" && <ExpertProfileTab expert={expert} />}
          {tab === "library" && (
            <SessionsTab profileId={expert.user && expert.user.profile && expert.user.profile.id} />
          )}
          {tab === "eposters" && (
            <EPostersTab profileId={expert.user && expert.user.profile && expert.user.profile.id} />
          )}
        </>
      )}
      {editExpertDialog && (
        <EditExpertDialog
          id={id}
          expert={expert}
          onClose={() => {
            setEditExpertDialog(false);
          }}
        />
      )}
      {editCoverDialog && (
        <EditImageDialog
          parentId={id}
          parentType="expert"
          imageType="cover"
          imageUrl={expert.cover}
          onClose={() => {
            setEditCoverDialog(null);
          }}
        />
      )}
      {editPublicAvatarDialog && (
        <EditImageDialog
          parentId={id}
          parentType="expert"
          imageType="public_avatar"
          imageUrl={expert.publicAvatar}
          onClose={() => {
            setEditPublicAvatarDialog(null);
          }}
        />
      )}
    </BaseLayout>
  );
};

export default ExpertPage;
